import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PartnerService, Partner } from '@/services/partnerService';
import { getPartnerLogoUrl } from '@/config/api';
import { useNavigate } from 'react-router-dom';

const PartnerPopup = () => {
  const [partner, setPartner] = useState<Partner | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const STORAGE_KEY = 'partnerPopupLastShown';
  const FIFTEEN_MINUTES = 15 * 60 * 1000; // 15 minutos em milissegundos

  // Verificar se é mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Carregar parceiros e mostrar popup
  const loadAndShowPartner = async () => {
    try {
      const partners = await PartnerService.getAllPartners();
      
      if (partners.length === 0) {
        return;
      }

      // Selecionar parceiro aleatório
      const randomIndex = Math.floor(Math.random() * partners.length);
      const selectedPartner = partners[randomIndex];
      
      setPartner(selectedPartner);
      setIsVisible(true);
      
      // Salvar timestamp no localStorage
      localStorage.setItem(STORAGE_KEY, Date.now().toString());

      // Auto-fechar após 10 segundos
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 10000);
    } catch (error) {
      console.error('Erro ao carregar parceiros para popup:', error);
    }
  };

  // Verificar se deve mostrar o popup baseado no localStorage
  useEffect(() => {
    if (isMobile) {
      return;
    }

    const checkAndShow = () => {
      const lastShownStr = localStorage.getItem(STORAGE_KEY);
      const now = Date.now();
      
      if (!lastShownStr) {
        // Primeira vez - mostrar imediatamente
        loadAndShowPartner();
        return;
      }

      const lastShown = parseInt(lastShownStr, 10);
      const timeSinceLastShown = now - lastShown;

      // Se passaram 15 minutos desde a última exibição, mostrar novamente
      if (timeSinceLastShown >= FIFTEEN_MINUTES) {
        loadAndShowPartner();
      }
    };

    // Verificar imediatamente ao montar o componente
    checkAndShow();

    // Verificar a cada minuto se já passaram 15 minutos
    intervalRef.current = setInterval(checkAndShow, 60 * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isMobile]);

  const handleClose = () => {
    setIsVisible(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleClick = () => {
    if (partner) {
      navigate(`/parceiro/${partner.id}`);
      handleClose();
    }
  };

  // Não renderizar em mobile
  if (isMobile || !isVisible || !partner) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-5 fade-in duration-300">
      <Card className="w-80 shadow-2xl border-2 border-primary/20 bg-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Logo do parceiro */}
            <div 
              className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-background border border-border cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleClick}
            >
              <img
                src={getPartnerLogoUrl(partner.logo)}
                alt={partner.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/placeholder.svg';
                }}
              />
            </div>

            {/* Conteúdo */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <h4 
                    className="font-semibold text-sm text-foreground cursor-pointer hover:text-primary transition-colors line-clamp-1"
                    onClick={handleClick}
                  >
                    {partner.name}
                  </h4>
                  {partner.category && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {partner.category}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 flex-shrink-0"
                  onClick={handleClose}
                  aria-label="Fechar"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              {partner.description && (
                <p 
                  className="text-xs text-muted-foreground line-clamp-2 mb-2 cursor-pointer hover:text-foreground transition-colors"
                  onClick={handleClick}
                  dangerouslySetInnerHTML={{ __html: partner.description }}
                />
              )}

              <Button
                size="sm"
                className="w-full text-xs h-7"
                onClick={handleClick}
              >
                Ver Detalhes
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerPopup;

